import React, { useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { 
  Loader2, 
  AlertCircle,
  Check,
  ArrowUpCircle,
  Calendar,
  Crown,
  RefreshCw
} from 'lucide-react';

const UpdateUserPlanPage: React.FC = () => {
  const { supabase } = useSupabase();
  const [email, setEmail] = useState('');
  const [planType, setPlanType] = useState<'basic' | 'professional' | 'premium' | 'trial' | 'free' | 'lifetime'>('professional');
  const [durationDays, setDurationDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      const { data, error } = await supabase.functions.invoke('admin-update-user', {
        body: {
          email,
          planType,
          durationDays
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        throw error;
      }

      setSuccess(`Plano do usuário ${email} atualizado com sucesso para ${planType.charAt(0).toUpperCase() + planType.slice(1)}`);
      setEmail('');
    } catch (err: any) {
      console.error('Erro ao atualizar plano:', err);
      setError(err.message || 'Erro ao atualizar plano do usuário');
    } finally {
      setLoading(false);
    }
  };

  // Calcula a data de expiração com base no tipo de plano e duração
  const calculateExpirationDate = () => {
    const now = new Date();
    
    // Para planos lifetime e free, não há expiração
    if (planType === 'lifetime' || planType === 'free') {
      return null;
    }
    
    // Para trial e planos pagos, adiciona os dias de duração
    now.setDate(now.getDate() + durationDays);
    return now.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Calcula a duração total em dias com base no tipo de plano
  const getTotalDuration = () => {
    switch (planType) {
      case 'premium':
        return 12 * 30; // 12 meses em dias (aproximado)
      case 'professional':
        return 12 * 30; // 12 meses em dias (aproximado)
      case 'basic':
        return 1 * 30; // 1 mês em dias
      case 'trial':
        return durationDays;
      default:
        return durationDays;
    }
  };

  // Calcula a porcentagem de progresso
  const getProgressPercentage = () => {
    if (planType === 'lifetime' || planType === 'free') {
      return 0; // Não mostra progresso para lifetime ou free
    }
    
    const totalDuration = getTotalDuration();
    return Math.min(100, Math.max(0, (durationDays / totalDuration) * 100));
  };

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Atualizar Plano de Usuário</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Ferramenta administrativa para atualizar planos de usuários
        </p>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          <p>{success}</p>
        </div>
      )}

      <div className="card p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email do Usuário *
              </label>
              <input
                type="email"
                id="email"
                className="input w-full"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="planType" className="block text-sm font-medium mb-1">
                Tipo de Plano *
              </label>
              <select
                id="planType"
                className="input w-full"
                value={planType}
                onChange={(e) => setPlanType(e.target.value as any)}
                required
              >
                <option value="basic">Básico</option>
                <option value="professional">Profissional</option>
                <option value="premium">Premium</option>
                <option value="trial">Trial</option>
                <option value="free">Gratuito</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </div>

            <div>
              <label htmlFor="durationDays" className="block text-sm font-medium mb-1">
                Duração (dias)
              </label>
              <input
                type="number"
                id="durationDays"
                className="input w-full"
                value={durationDays}
                onChange={(e) => setDurationDays(parseInt(e.target.value))}
                min="1"
                max="365"
              />
              <p className="text-xs text-gray-500 mt-1">
                Aplicável para planos Trial e pagos. Lifetime e Free ignoram este valor.
              </p>
            </div>

            {/* Visualização da expiração */}
            {planType !== 'lifetime' && planType !== 'free' && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-dark-light rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="font-medium">Previsão de Expiração</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Data: {calculateExpirationDate()}
                </p>
                
                {/* Barra de progresso */}
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Início</span>
                    <span>Duração Total: {getTotalDuration()} dias</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-dark-light rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-all duration-300"
                      style={{ width: `${getProgressPercentage()}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Hoje</span>
                    <span>{durationDays} dias</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6">
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Atualizando...
                </>
              ) : (
                <>
                  <ArrowUpCircle className="w-4 h-4 mr-2" />
                  Atualizar Plano
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-8 card p-6">
        <h2 className="text-lg font-bold mb-4">Informações sobre os Planos</h2>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
            <Crown className="w-5 h-5 text-yellow-500" />
            <div>
              <p className="font-medium">Premium</p>
              <p className="text-sm text-gray-500">Até 2 times, 100 jogadores por time, todas as funcionalidades</p>
              <p className="text-xs text-gray-500 mt-1">Duração: 12 meses (365 dias)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
            <Crown className="w-5 h-5 text-blue-500" />
            <div>
              <p className="font-medium">Profissional</p>
              <p className="text-sm text-gray-500">Até 2 times, 50 jogadores por time, todas as funcionalidades</p>
              <p className="text-xs text-gray-500 mt-1">Duração: 12 meses (365 dias)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
            <Crown className="w-5 h-5 text-gray-500" />
            <div>
              <p className="font-medium">Básico</p>
              <p className="text-sm text-gray-500">1 time, 30 jogadores, sem resenha</p>
              <p className="text-xs text-gray-500 mt-1">Duração: 1 mês (30 dias) - com todas as funcionalidades</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
            <Calendar className="w-5 h-5 text-orange-500" />
            <div>
              <p className="font-medium">Trial</p>
              <p className="text-sm text-gray-500">Período de avaliação com todas as funcionalidades</p>
              <p className="text-xs text-gray-500 mt-1">Duração: Personalizada (padrão: 21 dias)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
            <Crown className="w-5 h-5 text-purple-500" />
            <div>
              <p className="font-medium">Lifetime</p>
              <p className="text-sm text-gray-500">Acesso vitalício a todas as funcionalidades</p>
              <p className="text-xs text-gray-500 mt-1">Duração: Permanente</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateUserPlanPage;